import { checkPlacement, displayOf, isDocked, type NodeType, type StoryCtx } from '@dispositio/core';
import { de } from '../i18n/de';

/** Wie ein Node dieser Klasse entsteht: als normaler Node (z. B. Metadaten, Notiz), als lose Szene (Zeitpunkt bzw. Zeitraum) oder als Zeitstrahl. */
export type AddMode = 'normal' | 'event' | 'period' | 'timeline';

export interface AddClass {
  /** eindeutig in der Auswahl (die Klasse Szene kommt zweimal vor: als Ereignis und als Zeitraum) */
  key: string;
  /** Name in der Auswahl */
  label: string;
  type: NodeType;
  mode: AddMode;
}

/** Bereits vorhandenes Element einer Klasse, das sich als Kopie übernehmen lässt. */
export interface ExistingItem {
  id: string;
  title: string;
  sub: string;
}

/**
 * Klassen, die man im Node `currentId` anlegen kann: normale Nodes (Metadaten, eigene), die Szene (lose; auf einen Zeitstrahl gezogen
 * dockt sie dort an; nicht in einer Szene) und – nur in der Geschichte selbst – Zeitstrahl. Einträge (Person, Ort, eigene Objekte)
 * liegen nicht frei auf der Fläche: sie kommen über den Tab „Elemente“ oder @-Verweise in eine Szene.
 */
export function addableClasses(ctx: StoryCtx, currentId: string): AddClass[] {
  const current = ctx.nodes.get(currentId);
  if (!current || current.kind === 'timeline') return []; // Zeitstrahle haben keine eigene Fläche; Ereignisse und Zeiträume docken per Ziehen an
  const out: AddClass[] = [];
  const add = (type: NodeType, mode: AddMode, label = type.name): void => void out.push({ key: mode === 'event' || mode === 'period' ? `${type.id}:${mode}` : type.id, label, type, mode });
  for (const type of ctx.story.types) {
    switch (type.category) {
      case 'normal':
        if (!checkPlacement(ctx, 'normal', currentId)) add(type, 'normal');
        break;
      case 'scene':
        if (!checkPlacement(ctx, 'event', currentId)) {
          // eine Form: die Szene ist ein Zeitpunkt, bis sie ein Ende bekommt (PLAN.md §17)
          add(type, 'event', de.tools.event);
        }
        break;
      case 'timeline':
        if (current.kind === 'story') add(type, 'timeline');
        break;
      default:
    }
  }
  return out;
}

/** Vorhandene Elemente einer Klasse: Nodes mit ihrem Ort (Szenen in jeder Form). Zeitstrahle gibt es hier nur neu. */
export function existingFor(ctx: StoryCtx, cls: AddClass): ExistingItem[] {
  if (cls.mode === 'normal' || cls.mode === 'event' || cls.mode === 'period') {
    return ctx.story.nodes
      // Szenen: jede Form (die Form folgt der Zeit, PLAN.md §17)
      .filter((n) => n.typeId === cls.type.id && (cls.mode === 'normal' || n.kind === 'event' || n.kind === 'period'))
      .map((n) => {
        const parent = n.parentId ? ctx.nodes.get(n.parentId) : undefined;
        const where = parent ? (isDocked(ctx, n) ? `an der Zeitlinie „${displayOf(ctx, parent).title}“` : `in „${displayOf(ctx, parent).title}“`) : '';
        return { id: n.id, title: displayOf(ctx, n).title, sub: where };
      });
  }
  return [];
}

/** Größe, die eine Karte auf der Fläche braucht (Breite, Höhe) samt Abstand, damit neue Nodes nicht aufeinander landen. */
const SLOT = { w: 250, h: 130 };

/** Nächster freier Platz ab `from`: erst nach rechts, dann Zeile für Zeile nach unten. */
export function nearestFreePoint(taken: { x: number; y: number }[], from: { x: number; y: number }): { x: number; y: number } {
  for (let i = 0; i < 60; i++) {
    const p = { x: Math.round(from.x + (i % 5) * SLOT.w), y: Math.round(from.y + Math.floor(i / 5) * SLOT.h) };
    if (taken.every((t) => Math.abs(t.x - p.x) >= SLOT.w - 20 || Math.abs(t.y - p.y) >= SLOT.h - 20)) return p;
  }
  return { x: Math.round(from.x), y: Math.round(from.y) };
}
